import React, { useState } from 'react';
import { PDFDocument } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import { CloudUpload, CloudDownload, Description, Autorenew } from '@material-ui/icons';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const PDFCompressComponent = () => {
  const [file, setFile] = useState<File | null>(null);
  const [quality, setQuality] = useState('medium');
  const [loading, setLoading] = useState(false);
  const [originalSize, setOriginalSize] = useState(0);
  const [compressedSize, setCompressedSize] = useState(0);
  const [compressedPdfBytes, setCompressedPdfBytes]: any = useState<Uint8Array | null>(null);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setOriginalSize(selectedFile.size);
      setCompressedPdfBytes(null);
      setCompressedSize(0);
    } else {
      alert('Please select a valid PDF file');
    }
  };

  const getCompressionSettings = (qualityLevel: string) => {
    switch (qualityLevel) {
      case 'high':
        return { scale: 1.5, jpegQuality: 0.9 };
      case 'medium':
        return { scale: 1.0, jpegQuality: 0.6 };
      case 'low':
        return { scale: 0.7, jpegQuality: 0.3 };
      default:
        return { scale: 1.0, jpegQuality: 0.6 };
    }
  };

  const compressPDF = async () => {
    if (!file) {
      alert('Please upload a PDF file first');
      return;
    }

    setLoading(true);
    try {
      const arrayBuffer = await (file as any).arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const pdfDoc = await PDFDocument.create();
      const { scale, jpegQuality } = getCompressionSettings(quality);

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Canvas context not found');

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: context as any, viewport }).promise;

        // Convert to compressed image
        const imgDataUrl = canvas.toDataURL('image/jpeg', jpegQuality);
        const imgBytes = await fetch(imgDataUrl).then((res) => res.arrayBuffer());
        const pdfImage = await pdfDoc.embedJpg(imgBytes);

        const newPage = pdfDoc.addPage([viewport.width, viewport.height]);
        newPage.drawImage(pdfImage, {
          x: 0,
          y: 0,
          width: viewport.width,
          height: viewport.height,
        });
      }

      const pdfBytes = await pdfDoc.save();
      setCompressedPdfBytes(pdfBytes);
      setCompressedSize(pdfBytes.length);
    } catch (error) {
      console.error('Compression error:', error);
      alert('Failed to compress PDF. Please try another file.');
    } finally {
      setLoading(false);
    }
  };

  const downloadCompressedPDF = () => {
    if (!compressedPdfBytes || !file) return;

    const blob = new Blob([compressedPdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    // Get base name (remove extension from original file)
    const originalName = file.name.replace(/\.pdf$/i, '');
    const fileName = `compressed_${originalName}_${quality}.pdf`;

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const compressionRatio =
    originalSize && compressedSize
      ? Math.round((1 - compressedSize / originalSize) * 100)
      : 0;

  return (
    <section className="pcoded-main-container">
      <div className="pcoded-wrapper">
        <div className="">
          <div className="pcoded-inner-content">
            <div className="main-body">
              <div
                style={{
                  minHeight: '100vh',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  padding: '40px 20px',
                }}
              >
                <div
                  style={{
                    maxWidth: '700px',
                    margin: '0 auto',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      padding: '30px',
                      color: 'white',
                    }}
                  >
                    <h1
                      style={{
                        fontSize: '32px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px',
                        margin: 0,
                      }}
                    >
                      <Description style={{ fontSize: '40px' }} />
                      PDF Compressor
                    </h1>
                    <p style={{ marginTop: '10px', opacity: 0.9, fontSize: '16px' }}>
                      Reduce PDF file size instantly
                    </p>
                  </div>

                  <div style={{ padding: '40px' }}>
                    {/* File Upload Section */}
                    <div style={{ marginBottom: '30px' }}>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '14px',
                          color: '#333',
                          marginBottom: '12px',
                        }}
                      >
                        Upload PDF File
                      </label>
                      <div
                        style={{
                          position: 'relative',
                          border: '2px dashed #ccc',
                          borderRadius: '8px',
                          padding: '40px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.3s',
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.borderColor = '#667eea')
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.borderColor = '#ccc')
                        }
                      >
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={handleFileChange}
                          style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            opacity: 0,
                            cursor: 'pointer',
                          }}
                        />
                        <CloudUpload
                          style={{ fontSize: '60px', color: '#999', marginBottom: '15px' }}
                        />
                        <p style={{ color: '#666', fontSize: '16px', margin: 0 }}>
                          {file ? file.name : 'Click or drag PDF file here'}
                        </p>
                        {file && (
                          <p
                            style={{
                              fontSize: '14px',
                              color: '#999',
                              marginTop: '10px',
                            }}
                          >
                            Original Size: {formatFileSize(originalSize)}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Quality Selection */}
                    <div style={{ marginBottom: '30px' }}>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '14px',
                          color: '#333',
                          marginBottom: '12px',
                        }}
                      >
                        Compression Quality
                      </label>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr 1fr',
                          gap: '12px',
                        }}
                      >
                        {['high', 'medium', 'low'].map((level) => (
                          <button
                            key={level}
                            onClick={() => {
                              setQuality(level);
                              setCompressedPdfBytes(null);
                            }}
                            style={{
                              padding: '14px 20px',
                              borderRadius: '8px',
                              fontSize: '15px',
                              border: 'none',
                              cursor: 'pointer',
                              transition: 'all 0.3s',
                              backgroundColor:
                                quality === level ? '#667eea' : '#f3f4f6',
                              color: quality === level ? 'white' : '#333',
                              boxShadow:
                                quality === level
                                  ? '0 4px 12px rgba(102, 126, 234, 0.4)'
                                  : 'none',
                            }}
                          >
                            {level.charAt(0).toUpperCase() + level.slice(1)}
                          </button>
                        ))}
                      </div>
                      <p
                        style={{
                          fontSize: '12px',
                          color: '#999',
                          marginTop: '10px',
                        }}
                      >
                        Higher quality = larger file size, better image quality
                      </p>
                    </div>

                    {/* Compress Button */}
                    <button
                      onClick={compressPDF}
                      disabled={!file || loading}
                      style={{
                        width: '100%',
                        background:
                          loading || !file
                            ? '#ccc'
                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        padding: '18px',
                        borderRadius: '8px',
                        fontSize: '18px',
                        border: 'none',
                        cursor: loading || !file ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        boxShadow:
                          loading || !file
                            ? 'none'
                            : '0 4px 15px rgba(102, 126, 234, 0.4)',
                      }}
                    >
                      {loading ? (
                        <>
                          <Autorenew
                            style={{
                              animation: 'spin 1s linear infinite',
                              fontSize: '24px',
                            }}
                          />
                          Compressing...
                        </>
                      ) : (
                        'Compress PDF'
                      )}
                    </button>

                    {/* Results Section */}
                    {compressedPdfBytes && (
                      <div
                        style={{
                          marginTop: '30px',
                          backgroundColor: '#f0fdf4',
                          border: '1px solid #86efac',
                          borderRadius: '8px',
                          padding: '30px',
                        }}
                      >
                        <h3
                          style={{
                            color: '#166534',
                            marginBottom: '20px',
                            fontSize: '18px',
                            margin: '0 0 20px 0',
                          }}
                        >
                          ✓ Compression Complete!
                        </h3>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '20px',
                            marginBottom: '20px',
                          }}
                        >
                          <div>
                            <p
                              style={{
                                fontSize: '14px',
                                color: '#666',
                                margin: '0 0 5px 0',
                              }}
                            >
                              Original Size
                            </p>
                            <p
                              style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: '#333',
                                margin: 0,
                              }}
                            >
                              {formatFileSize(originalSize)}
                            </p>
                          </div>
                          <div>
                            <p
                              style={{
                                fontSize: '14px',
                                color: '#666',
                                margin: '0 0 5px 0',
                              }}
                            >
                              Compressed Size
                            </p>
                            <p
                              style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: '#16a34a',
                                margin: 0,
                              }}
                            >
                              {formatFileSize(compressedSize)}
                            </p>
                          </div>
                        </div>
                        <div
                          style={{
                            backgroundColor: 'white',
                            borderRadius: '6px',
                            padding: '15px',
                            marginBottom: '20px',
                          }}
                        >
                          <p
                            style={{
                              fontSize: '14px',
                              color: '#666',
                              margin: '0 0 8px 0',
                            }}
                          >
                            Reduction
                          </p>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '15px',
                            }}
                          >
                            <div
                              style={{
                                flex: 1,
                                backgroundColor: '#e5e7eb',
                                borderRadius: '999px',
                                height: '12px',
                                overflow: 'hidden',
                              }}
                            >
                              <div
                                style={{
                                  background:
                                    'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                                  height: '100%',
                                  width: `${compressionRatio}%`,
                                  transition: 'width 0.5s ease',
                                }}
                              />
                            </div>
                            <span
                              style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: '#16a34a',
                                minWidth: '60px',
                              }}
                            >
                              {compressionRatio}%
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={downloadCompressedPDF}
                          style={{
                            width: '100%',
                            backgroundColor: '#16a34a',
                            color: 'white',
                            padding: '16px',
                            borderRadius: '8px',
                            fontSize: '16px',
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'all 0.3s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px',
                            boxShadow: '0 4px 12px rgba(22, 163, 74, 0.3)',
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = '#15803d')
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor = '#16a34a')
                          }
                        >
                          <CloudDownload style={{ fontSize: '22px' }} />
                          Download Compressed PDF
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <style
                  dangerouslySetInnerHTML={{
                    __html: `
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PDFCompressComponent;
